import { get } from 'lodash';
import { func, object, string } from 'prop-types';
import { useState, useEffect } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Tooltip } from '@hivebrite/design-system/Tooltip';

import { shorthandParser } from 'hb-builder/utils/attributes';
import Icon from 'hb-react/shared/components/Icon';

import PaddingInput from '../ComposedInputs/PaddingInput';
import InputTitle from '../SettingsDecoration/InputTitle';

const Padding = ({
  alert: {
    enabled: withAlert,
    fillConditions: fillAlertConditions,
    message: alertMessage,
  } = {},
  defaultAttributes,
  displayedInputs = undefined,
  item,
  onChange,
  needResetAlignment = true,
  settingKey,
  title,
  shouldForceUpdateAllChildren = null, // TODO: remove this default value when adding types to this component
}) => {
  const setting =
    get(item?.attributes, settingKey) ||
    get(defaultAttributes, settingKey) ||
    0;

  const parserPadding = (value) => ({
    bottom: shorthandParser(value, 'bottom'),
    left: shorthandParser(value, 'left'),
    right: shorthandParser(value, 'right'),
    top: shorthandParser(value, 'top'),
  });

  const [paddingValue, setPaddingValue] = useState(parserPadding(setting));

  const handleChangePadding = ({ type, value }) => {
    setPaddingValue((prev) => {
      const newValue = { ...prev, [type]: value && parseInt(value, 10) };
      const stringValue = `${newValue.top || 0}px ${newValue.right || 0}px ${
        newValue.bottom || 0
      }px ${newValue.left || 0}px`;

      const alignmentToChange =
        type === 'top' || type === 'bottom'
          ? 'verticalAlign'
          : 'horizontalAlign';

      onChange({
        id: item.id,
        forceUpdateAllChildren: shouldForceUpdateAllChildren
          ? shouldForceUpdateAllChildren({ type, value })
          : false,
        values: [
          {
            settingKey: `attributes.${settingKey}`,
            value: stringValue,
          },
          needResetAlignment && {
            settingKey: `attributes.${alignmentToChange}`,
            value: null,
          },
        ].filter(Boolean),
      });

      return newValue;
    });
  };

  useEffect(() => {
    setPaddingValue(parserPadding(item.attributes[settingKey]));
  }, [item.attributes[settingKey]]);

  return (
    <Wrapper>
      <Box>
        <InputTitle
          id="settingsStylePadding"
          label={title}
          boxProps={{ mb: 3 }}
        />
        {withAlert && fillAlertConditions?.(paddingValue) && (
          <Tooltip childrenElement={alertMessage}>
            <StyledIcon type="exclamation" />
          </Tooltip>
        )}
      </Box>
      <PaddingInput
        id={`settingsStylePadding-${item.id}`}
        onChange={handleChangePadding}
        value={paddingValue}
        displayedInputs={displayedInputs}
      />
    </Wrapper>
  );
};

export default Padding;

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  mb: 4,
  width: '100%',
}))``;

Padding.propTypes = {
  defaultAttributes: object,
  item: object.isRequired,
  onChange: func.isRequired,
  settingKey: string.isRequired,
  title: string.isRequired,
};

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;
